import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0309 from "../../course-03/lesson-09/lesson";
import lesson0311 from "../../course-03/lesson-11/lesson";
import lesson0503 from "../lesson-03/lesson";
import lesson0504 from "../lesson-04/lesson";
import lesson0515 from "../lesson-15/lesson";

const linkLesson0309 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0309.id);
const linkLesson0311 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0311.id);
const linkLesson0503 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0503.id);
const linkLesson0504 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0504.id);
const linkLesson0515 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0515.id);

const theory = ` <p>
In <a href="${linkLesson0503}" target="_blank">Lesson 5.3</a>, the difference between specular and diffuse reflection was explained - see also <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-1.svg data-img-alt="Figure 1. Specular and diffuse reflection.">Figure 1</a> as a reminder. When a sound wave hits a (infinitely) large, hard, flat and smooth surface, specular reflection occurs, meaning that the wave is reflected in a single direction. As dictated by Snell’s law, the angle of the incident sound wave
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi mathvariant=italic>𝜃i</mi>
</math>
and the angle of the reflected sound wave
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi mathvariant=italic>𝜃r</mi>
</math>
are equal. The same happens in optics when a light ray hits a flat (plane) mirror. On the other hand, when the hard surface is not smooth, but has a certain roughness applied to it (e.g. an irregular texture or a geometric pattern), the sound wave will be reflected in many different directions. This phenomenon is called diffuse reflection or scattering.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-1.svg alt="Figure 1. Specular and diffuse reflection."><figcaption class=caption>Figure 1. Specular and diffuse reflection.</figcaption></figure>
<p>Architectural elements that create sound scattering are called acoustic diffusers. They can either be separate elements added to the room surfaces, or they can be integrated into the architecture. Diffusers can be used to attenuate late echoes in auditoria or eliminate flutter echoes between parallel walls. They are also often used in recording studios to make the spatial sound distribution more homogeneous and to reduce room modes at low frequencies (see <a href="${linkLesson0504}" target="_blank">Lesson 5.4</a>).
<h3 class=sectionHead>The underlying physics of acoustic diffusers</h3>
<p>On an intuitive level, it is not so difficult to understand that a surface with a complex surface structure will tend to “break up” the sound and scatter it around. The underlying physical phenomenon of diffraction, however, is more complicated and requires advanced mathematics to describe it correctly. As this goes beyond the scope of this course, we will limit ourselves here to a simple qualitative explanation of how scattering happens. A more in-depth explanation can be found in literature (see for example references [4] and [6]).<br class=newline>
<p>When a wave encounters an object, it interacts with that object. In physics, this phenomenon is commonly called diffraction. A typical example are breakwaters in the sea used for protecting harbours from large and powerful waves. In this case, incoming water waves arriving on one side of the breakwater are partially pushed back (“reflected”), and partially bent (“diffracted”) around the structure, leaving a “shadow zone” of calmer water on the protected side, i.e. behind the breakwater. Likewise, if we consider a sound wave hitting a flat surface of limited size, the sound field will be distorted by diffraction. This distortion can be imagined by placing secondary sources on the surface edges, which radiate the so-called diffraction waves. The interference between the specularly reflected wave from the surface and the diffraction waves leads to the scattered field. If the surface is larger than the wavelength (and the source and receiver are not too far away from the surface), then the specular wave will dominate, and we talk about specular reflection. On the other hand, if the surface is of similar size or even smaller than the wavelength (or the sound source and receiver are located at a large distance from the surface), then the diffracted waves will have a significant influence, causing sound to be reflected in many different directions: we say that scattering occurs.<br class=newline>
<p>From this we have learned that even a flat surface can produce some scattering, if its dimensions are smaller than the wavelength (or if the source and receiver are located far away). Now, if the surface is not flat, but contains regular or irregular bumps, coffers or other protrusions, each of those will also interact with the incoming sound wave. If these surface irregularities are much smaller compared to the wavelength, they will not disturb the specular reflection produced by the surface as a whole. On the other hand, if they are much bigger than the wavelength, then each protrusion face will produce a specular reflection of its own. In the intermediate case where the wavelength is similar to the size of protrusions, there will be interference between the scattered waves produced by each of the protrusions and the specular wave of the whole surface. The result is that a large fraction of the overall reflected energy is scattered in all directions. This is the physical background of how an acoustic diffuser works.<br class=newline>
<p>In the next section, we will look at some different types of acoustic diffusers.
<h3 class=sectionHead>Types of acoustic diffusers</h3>
<p>One could imagine an infinite amount of different shapes or structures that could be applied on a wall or ceiling and be used to create acoustic diffusion. Here, we will distinguish between five diffuser types: curved surfaces, irregular geometric structures, periodic geometric structures, alternating absorbing and reflecting structures and mathematical diffusers (see also [8]).
<h4 class=subsectionHead>Curved surfaces</h4>
<p>Curved surfaces can either be convex or concave. Each of the two types behaves differently in the acoustical sense. <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-2.svg data-img-alt="Figure 2. Reflected rays created by a flat, convex and concave surface.">Figure 2</a> compares the reflected rays for a flat, a convex and a concave surface with the source of incident sound located directly in front of the surface. For simplicity, only the cross-sections of these surfaces are shown in the figure. However, please have in mind that all these surfaces will diffuse sound in two dimensions, one of which is visible in the figure, while the other one is perpendicular to it and cannot be seen.<br class=newline>
<p>The amount of diffusion provided by a flat surface will tend to be small in both dimensions. A surface can be curved in one dimension, but essentially flat along the other. An example of a such a surface is the side surface of a cylinder (or a part of it). Such a surface will provide a small amount of diffusion along the dimension in which it is flat, but a large amount of diffusion along the dimension in which it is curved. For example, if diffusion is needed in the horizontal plane, upright cylinders can be used to provide it. If a surface is curved in both dimensions, it will provide a large amount of diffusion in both of them. An example of such a surface is the surface of a sphere (or a part of it).<br class=newline>
<p>The determination of whether a surface is convex or concave is made with respect to the position of the sound source. A convex surface always causes the reflected sound to spread out in space, and this spread is broader than the one produced by a flat surface with the same dimensions at the same location. In other words, a convex surface inherently behaves like a diffuser. The degree of diffusion depends on the radius of curvature and the length of the arc. As the same incident sound energy is distributed over a larger area, the reflected sound pressure level at any receiver point is lower than in the case of specular reflections bouncing off a flat surface. This effect is true for any acoustic diffuser: lower sound pressure level compared to the one provided by a flat reflector is the trade-off for wider spatial coverage.<br class=newline>
<p>
On the other hand, the acoustic behaviour of a concave surface is more complex. The reflected sound rays first converge, thus creating a zone with increased sound pressure level. This phenomenon is called focusing. For a concave surface made as a part of the side surface of a cylinder, there will be a set of locations having the maximum sound pressure. This is, in fact, the focal line of such a surface. If a concave surface is made as a part of the surface of a sphere, it will indeed have a single focal point. Once the sound has travelled beyond the focal point (or line), the sound rays start diverging and the sound pressure level drops off more quickly over distance than it would if it was reflected off a flat surface. In other words, at a sufficient distance – beyond its focal point/line – a concave surface starts to behave like an acoustic diffuser, similar to a convex surface. See also the principle part of <a href="${linkLesson0515}" target="_blank">Lesson 5.15</a>.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-2.svg alt="Figure 2. Reflected rays created by a flat, convex and concave surface."><figcaption class=caption>Figure 2. Reflected rays created by a flat, convex and concave surface.</figcaption></figure>
<p>Acoustic diffusion achieved using curved surfaces has a unique property that distinguishes this type of diffusers from all the other ones: the phase of the reflected wavefronts is maintained, with the consequence that the temporal envelope of the acoustic signal is fully preserved and not ‘smeared out’ in time. This means that the dispersion provided by a curved surface is purely spatial and not temporal. There is an ongoing scientific discussion as to the importance of phase (and time) preserving reflections on subjective perception. There seems to be more and more evidence that phase preservation is preferred for the best music listening experience (e.g. [7]).
<h4 class=subsectionHead>Irregular geometric structures</h4>
<p>
A hard material with a random surface texture, such as the rough surface shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-3.svg data-img-alt="Figure 3. Example of an irregular geometric structure.">Figure 3</a>, will provide efficient scattering at frequencies higher than a given limit frequency. This limit frequency is determined mainly by the amplitude or depth
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>d</mi>
</math>
of the texture pattern. The scattering that occurs will follow Lambert’s cosine law (see <a href="${linkLesson0309}" target="_blank">Lesson 3.9</a>) in good approximation.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-3.svg alt="Figure 3. Example of an irregular geometric structure."><figcaption class=caption>Figure 3. Example of an irregular geometric structure.</figcaption></figure>
<p>As a general rule of thumb, the reflection will remain specular as long as the pattern depth is smaller than one eighth of the wavelength:
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
       <mi>d</mi>
       <mo class=MathClass-rel><</mo>
       <mi>λ</mi>
       <mo class=MathClass-bin>∕</mo>
       <mn>8</mn>
       <mspace/>
       <mtext>&nbsp;or&nbsp;</mtext>
       <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mi>c</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>8</mn>
      <mi>d</mi>
      </math>
</table>
<p>
In other words, at low enough frequencies the sound waves will be reflected by the rough surface as if it were a flat surface. As the frequency increases, the reflection pattern becomes more and more diffuse. Efficient scattering occurs above the limiting frequency at which the pattern depth
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>d</mi>
</math>
is equal to the quarter of the wavelength:
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
      <mi>d</mi>
      <mo class=MathClass-rel>></mo>
      <mi>λ</mi>
      <mo class=MathClass-bin>∕</mo>
      <mn>4</mn>
       <mtext>&nbsp;or&nbsp;</mtext>
       <mi>f</mi>
       <mo class=MathClass-rel>></mo>
       <mi>c</mi>
       <mo class=MathClass-bin>∕</mo>
       <mn>4</mn>
       <mi>d</mi>
      </math>
</table>
<p>In conclusion, any random pattern will provide good scattering at high enough frequencies. For precise calculation of the acoustic diffusion pattern or quantification of the degree of scattering, wave equation solving numerical methods should be used (see <a href="${linkLesson0311}" target="_blank">Lesson 3.11</a>).
<p>
A side-effect of the random texture pattern with deep wells is that, in addition to diffusion, some - potentially undesirable – residual absorption will occur due to acoustic resonances in the “wells” of the pattern, friction of the air particles “rubbing” against the well walls etc. At certain frequencies the sound absorption coefficient can easily go up to 0.2 or 0.3. The acoustic designer should be aware of these residual absorption effects. If the pattern is to be applied to large surfaces, it may be a good idea to have the sound absorption coefficient of such a diffuser measured in a reverberation chamber on a 10
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
     <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msup>
</math>
sample.
<p>It should also be noted that diffuse reflections from rough surfaces, as with all fine scale diffusion, do not preserve phase, which may have an impact on the subjective perception (see section <span class=cmti-10>Subjective effects of diffusion</span>).
<h4 class=subsectionHead>Periodic geometric structures</h4>
<p>
Hard surfaces with a superimposed regular surface pattern will also produce scattering. As with irregular patterns, the pattern depth
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>d</mi>
</math>
can give an initial indication on the frequency at which diffusion starts to occur. However, the period
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>p</mi>
</math>
of the regular pattern (i.e. the spacing between identical geometric elements) also has an influence on the frequency-dependent behaviour of the diffuser. As an approximate guideline for diffuser design, the following dimensioning frequency can be considered [8]:
<table class=equation>
<tr>
   <td>
      <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>500</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>p</mi>
      </math>
</table>
<p>The lower limiting frequency can be approximated as follows:
<table class=equation>
<tr>
<td>
   <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
   <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>lower</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
</table>
<p>The upper limiting frequency is closely linked to the shape of the geometric elements. Of the examples shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-4.svg data-img-alt="Figure 4. Examples of periodic geometric structures.">Figure 4</a>, the semi-cylinders have the widest frequency range, while, in comparison, the diffusive behaviour of the rectangular pattern is limited to a fairly narrow frequency range.
<p>The upper limiting frequencies for the three structures can be given as a rule of thumb:
<ul class=itemize1>
<li class=itemize>
   Rectangles:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>upper</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
<li class=itemize>
   Triangles:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>upper</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>5</mn>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
<li class=itemize>
   Semi-cylinders:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>upper</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <mn>5</mn>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
</ul>
<p>
To gain a better understanding of the scattering behaviour, wave based numerical calculation methods are required.<br class=newline>
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-4.svg alt="Figure 4. Examples of periodic geometric structures."><figcaption class=caption>Figure 4. Examples of periodic geometric structures.</figcaption></figure>
<p>As with diffusers made as curved surfaces, the periodic structures can also be designed as one-dimensional or two-dimensional diffusers.<br class=newline>Extruded diffusers with periodic pattern developed along one dimension will only produce scattering in one plane. For example, vertically extruded elements will produce diffusion in the horizontal plane, while horizontally extruded elements only provide diffusion in the vertical plane. As the diffusion effect can be heard and measured in one plane, it can be said that one-dimensional periodic structures provide two-dimensional diffusion.<br class=newline>Two-dimensional diffusers have their periodic pattern developed along two dimensions. As such, they provide diffusion in two perpendicular planes simultaneously, and, by extension, in the entire space. Therefore, it can be said that two-dimensional periodic structures provide three-dimensional diffusion.<br class=newline>
<p>It is sometimes said that periodic diffusers can produce audible coloration of sound, especially if the pattern is repeated over large surfaces. This could be attributed either to phase effects happening at certain frequencies, or to residual absorption occurring in the wells between adjacent elements. In critical cases, it may be a good idea to measure the sound absorption (and possibly diffusion) coefficients of a sample in a reverberation chamber.
<h4 class=subsectionHead>Alternating absorbing and reflecting materials</h4>
<p>
Diffusive behaviour can be obtained using surfaces that are not necessarily textured. For example, if a flat surface is made of alternating strips of absorbing and reflecting material, as shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-5.svg data-img-alt="Figure 5. Alternating absorbing and reflecting materials.">Figure 5</a>, efficient scattering will occur (in addition to absorption) even though the surface is essentially flat. If the spacing of the bands is regular with the period
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>p</mi>
</math>
, the following dimensioning frequency
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
and frequency limits can be used as a design guideline:<br class=newline>
<p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mn>500</mn>
   <mo class=MathClass-bin>∕</mo>
   <mi>p</mi>
</math>
<br class=newline>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>lower</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>0</mn>
   <mo class=MathClass-punc>.</mo>
   <mn>5</mn>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
<br class=newline>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>upper</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>3</mn>
   <mo class=MathClass-punc>.</mo>
   <mn>5</mn>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
<br class=newline>
<p>
In addition, sufficient depth of the absorbing bands is required if diffusion (and absorption) is to be obtained at low frequencies.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-5.svg alt="Figure 5. Alternating absorbing and reflecting materials."><figcaption class=caption>Figure 5. Alternating absorbing and reflecting materials.</figcaption></figure>
<h4 class=subsectionHead>Mathematical diffusers</h4>
<p>In the 1970s and 1980s, considerable research was done on the design of phase-grating diffusers based on mathematical sequences. Diffusers based on these mathematical theories are often named Schroeder diffusers after their inventor. The main advantages of these diffusers were manifold at the time: their scattering could be predicted by relatively simple equations, and their performance could be optimised. In addition, they were straightforward to build.<br class=newline>
<p>
One such example of a Schroeder diffuser that is often found in recording studios is the Quadratic Residue Diffuser (QRD) [4]. A QRD diffuser consists of a series of wells of equal width, separated by fins. The wells have different depths, causing plane waves running down each well to be reflected with a different time lag, resulting in phase changes. The reflected waves travel back up the wells and are reradiated into space. The interference between all the reradiated waves from each well determine the diffusion pattern. Schroeder demonstrated that the energy reflected in the different directional lobes is the same if the well depths follow the mathematical sequence
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>n</mi>
   <mo class=MathClass-rel>=</mo>
   <mi mathvariant=italic>mod</mi>
   <mo class=MathClass-open>(</mo>
   <msup>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msup>
   <mo class=MathClass-bin>∕</mo>
   <mi>N</mi>
   <mo class=MathClass-close>)</mo>
</math>
, where m = 0, 1, 2, 3… and N is a prime number (in practice often 7, 11, 13, 17, 19 or 23). Here “mod” is the modulo operator, returning the remainder of the division of
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
      <mrow>
         <mi>m</mi>
      </mrow>
      <mrow>
         <mn>2</mn>
      </mrow>
   </msup>
</math>
by N. As an example, <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-6.svg data-img-alt="Figure 6. Section through a QRD diffuser of order N = 7. The number sequences that generate the pattern are shown at the top.">Figure 6</a> shows two periods of an N = 7 QRD diffuser. <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-7.svg data-img-alt="Figure 7. Typical QRD diffuser. Source: www.rpgeurope.com.">Figure 7</a> shows the practical implementation of this design. Notice that the wells at each end (their depth is actually zero) are half as wide, compared to all the others. This design change was made to obtain a symmetrical appearance of each individual diffuser element, and to allow the elements to be arranged into larger structures by putting them next to each other, thus continuing the designed QRD sequence.
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-6.svg alt="Figure 6. Section through a QRD diffuser of order N = 7. The number sequences that generate the pattern are shown at the top."><figcaption class=caption>Figure 6. Section through a QRD diffuser of order N = 7. The number sequences that generate the pattern are shown at the top.</figcaption></figure>
<p>
As a guideline, the design frequency
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
, and the lower and upper limiting frequencies of a QRD diffuser can be given as follows [8]:<br class=newline><br class=newline>
<p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mo class=MathClass-open>(</mo>
   <mn>172</mn>
   <mo class=MathClass-bin>⋅</mo>
   <msub>
      <mrow>
         <mi>n</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>max</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
   <mo class=MathClass-bin>∕</mo>
   <mo class=MathClass-open>(</mo>
   <mi>N</mi>
   <mo class=MathClass-bin>⋅</mo>
   <msub>
      <mrow>
         <mi>d</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>max</mi>
      </mrow>
   </msub>
   <mo class=MathClass-close>)</mo>
</math>
<br class=newline>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>lower</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>0</mn>
   <mo class=MathClass-punc>.</mo>
   <mn>5</mn>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mn>0</mn>
      </mrow>
   </msub>
</math>
<br class=newline>
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>upper</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>≈</mo>
   <mn>573</mn>
   <mo class=MathClass-bin>∕</mo>
   <mi>w</mi>
</math>
<br class=newline>
<p>
Here
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>w</mi>
</math>
is the width of each individual well (in meters),
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>n</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>max</mi>
      </mrow>
   </msub>
</math>
is the maximum value of
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>n</mi>
</math>
in the period sequence, and
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msub>
      <mrow>
         <mi>d</mi>
      </mrow>
      <mrow>
         <mi mathvariant=italic>max</mi>
      </mrow>
   </msub>
</math>
is the depth of the deepest well (in meters) in the structure.<br class=newline>
<p>The original QRD diffusers have thin “fins” between the wells in order to define the form of each well, separate adjacent wells from each other, and minimise interaction between sound waves traveling down and up each well. However, it is possible to omit these fins altogether. An in-depth discussion of the consequences of this is given in reference [4].<br class=newline>
<p>
Despite being very efficient diffusers, it should be noted that QRD diffusers also exhibit a significant degree of residual absorption. In addition, due to the phase grating principle, they do not preserve the temporal envelope of the diffuse reflections, which may have implications on the subjective perception of music.<br class=newline>
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-7.svg alt="Figure 7. Typical QRD diffuser. Source: www.rpgeurope.com."><figcaption class=caption>Figure 7. Typical QRD diffuser. Source: www.rpgeurope.com.</figcaption></figure>
<h3 class=sectionHead>Reflection patterns for different diffuser types</h3>
<p>In the principle part of this lesson displayed on the right hand side, you can play around with a number of different diffuser shapes, and see how incident sound is more or less diffusely reflected at different frequencies. The images show computer simulations of the reflection patterns. The colour scale is linked to the strength of the reflected sound.<br class=newline>
<p>The reflector is located on the right, and the source is on the bottom left. <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-8.svg data-img-alt="Figure 8. Theoretical specular reflection from a flat surface, not taking into account edge diffraction due to the limited extent of the surface. In reality, such behaviour only occurs at sufficiently high frequencies, where the wavelength is smaller than the dimensions of the reflector.">Figure 8</a> shows the case of the purely theoretical specular reflection: a perfect “beam” of reflected sound is created (indicated in red), representing a binary situation. Anywhere within the beam the reflection is present, while outside of the beam no reflection is present. This corresponds to a high-frequency approximation. By comparing the coloured reflection patterns shown in the principle part with the ideally specular behaviour shown in <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-8.svg data-img-alt="Figure 8. Theoretical specular reflection from a flat surface, not taking into account edge diffraction due to the limited extent of the surface. In reality, such behaviour only occurs at sufficiently high frequencies, where the wavelength is smaller than the dimensions of the reflector.">Figure 8</a>, it can be seen how much the real reflection/diffusion behaviour differs from the theoretical specular case. As an example, <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-9.svg data-img-alt="Figure 9. True reflection pattern for a 3m flat reflector at 1000 Hertz (calculated using the Boundary Element Method (BEM)).">Figure 9</a> shows the real reflection pattern of a 3-meter wide flat panel at 1000 Hertz. It can be seen that the reflection is indeed close to being specular, with some “lobing” effects occurring that make the sound distribution less homogeneous compared to the theoretical specular beam.<br class=newline>
<p>
By exploring the principle, you will get a feel of the scattering efficiency of a certain shape at a certain frequency.<br class=newline>
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-8.svg alt="Figure 8. Theoretical specular reflection from a flat surface, not taking into account edge diffraction due to the limited extent of the surface. In reality, such behaviour only occurs at sufficiently high frequencies, where the wavelength is smaller than the dimensions of the reflector."><figcaption class=caption>Figure 8. Theoretical specular reflection from a flat surface, not taking into account edge diffraction due to the limited extent of the surface. In reality, such behaviour only occurs at sufficiently high frequencies, where the wavelength is smaller than the dimensions of the reflector.</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/05/09/theory/figure-9.svg alt="Figure 9. True reflection pattern for a 3m flat reflector at 1000 Hertz (calculated using the Boundary Element Method (BEM))."><figcaption class=caption>Figure 9. True reflection pattern for a 3-meter wide flat reflector at 1000 Hertz (calculated using the Boundary Element Method (BEM)).</figcaption></figure>
<h3 class=sectionHead>Subjective effects of diffusion</h3>
<p>Providing an adequate amount of diffusion in the right locations can clearly have positive effects on the subjective aural impression and quality of a room. Diffusers can reduce certain acoustic faults, such as flutter echoes between parallel walls or distinct late echoes coming from a far reflective surface. They can also take off the “sharpness” of strong reflections created by large smooth surfaces (e.g. a concrete wall), behaving like acoustic mirrors. However, if applied too abundantly or in the wrong locations, diffusers may also cause a room to lose its acoustic signature or personality, if they reduce amplitude of early reflections and/or scramble their phase too much. In the end, the choice of how many diffusers to use and where to position them depends on what one is trying to achieve in a room, from the acoustical standpoint.<br class=newline>
<p>In the listening room of a studio, for example, one generally wants to avoid early specular reflections from the loudspeakers to the listening or mixing position. While absorption can be used to eliminate early reflections, too much absorption in the studio will result in an extensively damped room similar to an anechoic chamber, and consequently a very dry sound. In this case, diffusers should be used complementary to absorbers on some surfaces, leaving a minimum amount of weak diffuse reflections in the room. Diffusers also help to make the sound distribution in the studio more homogeneous and contribute in reducing room modes.<br class=newline>
<p>
In concert halls, the use of diffusers is a more complicated subject. Historically, the concert halls of the
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
   <mrow>
         <mn>19</mn>
      </mrow>
      <mrow>
         <mi mathvariant=italic>th</mi>
      </mrow>
   </msup>
</math>
century, such as the Amsterdam Concertgebouw or the Goldener Saal of the Musikverein in Vienna, have a significant amount of sound scattering provided by decorative elements (mouldings, columns, statues, coffered ceilings, curved vaults…), in addition to smooth flat surfaces providing strong early reflections (see <a href="#" data-img-src=${config.ASSETS_URL}/05/09/theory/figure-10.jpg data-img-alt="Figure 10. Goldener Saal of the Musikverein in Vienna (Source: Wikipedia).">Figure 10</a>). Given the unanimously praised acoustic quality of these concert halls, clearly a good balance between specular and diffuse reflections was struck. In the second half of the
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <msup>
   <mrow>
         <mn>20</mn>
      </mrow>
      <mrow>
         <mi mathvariant=italic>th</mi>
      </mrow>
   </msup>
</math>
century, new concert halls were being built with many plain flat surfaces and very little ornamentation. The acoustics of these new halls were often found to be unpleasant, which could in part be explained by acoustical “glare” [5] arising from the plain flat surfaces. In the 1980s and 1990s, the use of ample surface texture was favoured by many acousticians as a general solution to avoid acoustical glare in concert halls, even with a tendency towards “the more diffusion, the better”. More recent research and discussion now seems to indicate that there are indeed limits to the use of surface texture or other fine scale diffusion. The known side-effects are that small scale diffusion tends to distort the temporal envelope of early reflections resulting in less engaging acoustics, and that residual absorption can occur, thus creating tone colouration. In addition, diffusion on the stage walls tends to keep more sound energy on stage rather than projecting it out to the audience. While there is no consensus yet among experts on the use of diffusion in concert halls, this topic is discussed in more detail in literature, see for example references [7] and [5].
<figure class=figure>
<p><img src=${config.ASSETS_URL}/05/09/theory/figure-10.jpg alt="Figure 10. Goldener Saal of the Musikverein in Vienna (Source: Wikipedia)."><figcaption class=caption>Figure 10. Goldener Saal of the Musikverein in Vienna (Source: Wikipedia).</figcaption></figure>
<h3 class=sectionHead>Quantification of the acoustic performance of a diffuser</h3>
<p>The scattering from a surface or diffuser can be characterised by means of the scattering coefficient (s) or the diffusion coefficient (d). Both coefficients describe a different aspect of the reflection behaviour of the surface. In both cases they are simplified representations of the true reflection behaviour. The naming of these coefficients goes back to their historic development during the 1990s by two different research teams, and the distinction has been kept as such ever since, despite the fact that the terms “diffusion” and “scattering” are essentially synonyms.<br class=newline>
<p>
The sound scattering coefficient (s) is defined as the ratio between the acoustic energy reflected in non-specular directions and the total reflected acoustic energy. A surface can generally be considered to be sound scattering if
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>s</mi>
   <mo class=MathClass-rel>≥</mo>
   <mn>0</mn>
   <mo class=MathClass-punc>.</mo>
   <mn>5</mn>
</math>
. The scattering coefficient is commonly used in room acoustic modelling software such as Odeon or CATT Acoustics. It only tells how much energy is scattered, not how spatially uniform this scattering is.<br class=newline>
<p>The diffusion coefficient (d) is a measure of the uniformity of the reflected sound. A surface producing mainly specular reflections will have the diffusion coefficient close to 0, while a perfectly hemispherical diffuser will have the diffusion coefficient equal to 1. In essence, the diffusion coefficient is the single-number quantity that stems from evaluating the quite complex polar response of the scattering from an architectural surface. This allows the acousticians and diffuser designers to design and compare the performance of different diffusers, and to use this information in room acoustic design and performance specification.<br class=newline>
<p>Since the reflection/diffusion behaviour of a surface is highly wavelength- (frequency-) dependent, the scattering and diffusion coefficients are expressed as frequency-dependent quantities, i.e. as single values in all frequency bands of interest.<br class=newline>
<p>The international standard ISO 17497 ([1], [2]) describes in detail how the scattering coefficient (s) and the diffusion coefficient (d) should be measured in laboratory conditions, i.e. in a reverberation chamber and an anechoic chamber, respectively.<br class=newline>
<p>The diffusion coefficient (d) can also be simulated using wave-based computational models such as the Boundary Element Method (BEM). Please refer to <a href="${linkLesson0311}" target="_blank">Lesson 3.11</a> and reference [4]. The diffusion patterns shown in the principle part of this lesson were created using BEM simulations.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
<dt>
 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <mi mathvariant=italic>𝜃i</mi>
 </math>
<dd>angle of the incident sound wave on the surface
<dt>
 <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
     <mi mathvariant=italic>𝜃r</mi>
 </math>
<dd>angle of the reflected sound wave on the surface
<dt>BEM
<dd>Boundary Element Method
<dt>d
<dd>diffusion coefficient
<dt>QRD
<dd>Quadratic Residue Diffuser
<dt>s
<dd>scattering coefficient
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
<dt class=thebibliography id=X0-ISO17497-1>[1]
<dd class=thebibliography id=bib-1>
   <p><span class=cmti-10>Acoustics - Sound-scattering properties of surfaces - Part 1: Measurement </span><span class=cmti-10>of the random-incidence scattering coefficient in a reverberation room</span>. 2004.
<dt class=thebibliography id=X0-ISO17497-2>[2]
<dd class=thebibliography id=bib-2>
   <p><span class=cmti-10>Acoustics - Sound-scattering properties of surfaces - Part 2: Measurement </span><span class=cmti-10>of the directional diffusion coefficient in a free field</span>. 2012.
<dt class=thebibliography id=X0-Beranek>[3]
<dd class=thebibliography id=bib-3>
   <p>Leo Beranek. “Concert hall acoustics”. In: <span class=cmti-10>JASA</span>. 92 (1). July 1992, pp. 1–39.
<dt class=thebibliography id=X0-Cox>[4]
<dd class=thebibliography id=bib-4>
   <p>Trevor Cox and Peter D’Antonio. <span class=cmti-10>Acoustic Absorbers and Diffusers</span>. 2. ed., London: Spon Press, 2009.
<dt class=thebibliography id=X0-Kahle>[5]
<dd class=thebibliography id=bib-5>
   <p>Eckhard Kahle. “Halls without qualities - or the effect of acoustic diffusion”. In: <span class=cmti-10>Proceedings of the Institute of Acoustics</span>. Vol. 40. Pt. 3. 2018.
<dt class=thebibliography id=X0-Kuttruff>[6]
<dd class=thebibliography id=bib-6>
   <p>Heinrich Kuttruff. <span class=cmti-10>Room Acoustics</span>. 4. ed., London: Spon Press, 2000.
<dt class=thebibliography id=X0-Lokki>[7]
<dd class=thebibliography id=bib-7>
   <p>Tapio Lokki. “Engaging concert hall acoustics is made up of temporal envelope preserving reflections.” In: <span class=cmti-10>JASA</span>. 29 (6). June 2011.
<dt class=thebibliography id=X0-Rindel>[8]
<dd class=thebibliography id=bib-8>
   <p>Jens Holger Rindel. DTU lecture notes. 1999.
</dl>`;

export default theory;
